import { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Trash2, Save, Loader2, ShieldAlert, Crown } from "lucide-react";
import { Toaster, toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/admin/AuthContext";
import { Field, TextInput } from "@/admin/EditorShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UsersManager() {
  const { user: me } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ email: "", password: "", name: "", role: "editor" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/auth/admins`, { withCredentials: true });
      setAdmins(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onCreate = async (e) => {
    e.preventDefault();
    if (!draft.email || draft.password.length < 8 || !draft.name) {
      toast.error("Compila email, nome e password (min 8 caratteri)");
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/auth/admins`, draft, { withCredentials: true });
      setDraft({ email: "", password: "", name: "", role: "editor" });
      toast.success("Admin creato");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setCreating(false);
    }
  };

  const onDelete = async (id, label) => {
    if (!window.confirm(`Eliminare l'admin "${label}"?`)) return;
    try {
      await axios.delete(`${API}/auth/admins/${id}`, { withCredentials: true });
      toast.success("Admin eliminato");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const onChangeRole = async (id, role) => {
    try {
      await axios.patch(`${API}/auth/admins/${id}`, { role }, { withCredentials: true });
      toast.success("Ruolo aggiornato");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const onResetPassword = async (id) => {
    const newPassword = window.prompt("Nuova password (min 8 caratteri):");
    if (!newPassword) return;
    if (newPassword.length < 8) {
      toast.error("Password troppo corta");
      return;
    }
    try {
      await axios.patch(
        `${API}/auth/admins/${id}`,
        { password: newPassword },
        { withCredentials: true },
      );
      toast.success("Password aggiornata");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  return (
    <div data-testid="admin-users-page">
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: { background: "rgba(16,16,22,0.95)", border: "1px solid rgba(212,175,55,0.4)", color: "#F5F5F5" },
        }}
      />

      <p className="font-mono-pl text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">Owner only</p>
      <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-wide mt-2">
        Gestione Admin
      </h1>
      <p className="mt-2 text-sm text-[#B6B6BE] max-w-2xl">
        Crea, modifica o rimuovi gli admin del pannello. Massimo 3 consigliato.
      </p>

      {/* Existing */}
      <div className="mt-9 rounded-3xl p-6 sm:p-8" style={{ background: "linear-gradient(180deg, rgba(22,22,29,0.6), rgba(11,11,15,0.85))", border: "1px solid rgba(255,255,255,0.06)" }}>
        {loading ? (
          <div className="flex items-center gap-2 text-[#B6B6BE] text-sm">
            <Loader2 size={14} className="animate-spin" /> Caricamento…
          </div>
        ) : (
          <ul className="divide-y divide-white/[0.06]">
            {admins.map((a) => (
              <li key={a.id} className="py-4 flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-3 flex-1 min-w-[200px]">
                  <span
                    className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                      a.role === "owner"
                        ? "bg-[#D4AF37]/15 border border-[#D4AF37]/45 text-[#D4AF37]"
                        : "bg-[#00D4FF]/10 border border-[#00D4FF]/35 text-[#00D4FF]"
                    }`}
                  >
                    {a.role === "owner" ? <Crown size={14} /> : <ShieldAlert size={14} />}
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-[#F5F5F5]">{a.name}</p>
                    <p className="text-xs text-[#B6B6BE]">{a.email}</p>
                  </div>
                </div>
                <select
                  value={a.role}
                  onChange={(e) => onChangeRole(a.id, e.target.value)}
                  disabled={a.id === me?.id}
                  className="px-3 py-2 rounded-lg bg-[#0B0B0F]/90 border border-white/10 text-xs uppercase tracking-wider text-[#F5F5F5] disabled:opacity-50"
                >
                  <option value="owner">Owner</option>
                  <option value="editor">Editor</option>
                </select>
                <button
                  onClick={() => onResetPassword(a.id)}
                  className="text-xs uppercase tracking-[0.18em] px-3 py-2 rounded-full border border-white/10 text-[#B6B6BE] hover:text-[#F5F5F5]"
                >
                  Reset password
                </button>
                <button
                  onClick={() => onDelete(a.id, a.email)}
                  disabled={a.id === me?.id}
                  className="text-xs uppercase tracking-[0.18em] px-3 py-2 rounded-full border border-[#E26B6B]/35 text-[#E26B6B] hover:bg-[#E26B6B]/10 disabled:opacity-30 inline-flex items-center gap-1.5"
                >
                  <Trash2 size={12} /> Elimina
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Create */}
      <form
        onSubmit={onCreate}
        className="mt-7 rounded-3xl p-6 sm:p-8"
        style={{ background: "linear-gradient(180deg, rgba(22,22,29,0.6), rgba(11,11,15,0.85))", border: "1px solid rgba(212,175,55,0.18)" }}
      >
        <p className="font-mono-pl text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] mb-3">
          Nuovo Admin
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Nome">
            <TextInput value={draft.name} onChange={(x) => setDraft({ ...draft, name: x })} placeholder="Mario" />
          </Field>
          <Field label="Email">
            <TextInput type="email" value={draft.email} onChange={(x) => setDraft({ ...draft, email: x })} placeholder="mario@purelife.it" />
          </Field>
          <Field label="Password (min 8)">
            <TextInput type="password" value={draft.password} onChange={(x) => setDraft({ ...draft, password: x })} />
          </Field>
          <Field label="Ruolo">
            <select
              value={draft.role}
              onChange={(e) => setDraft({ ...draft, role: e.target.value })}
              className="search-input w-full px-3 py-3 rounded-xl bg-[#0B0B0F]/90 border border-white/10 text-sm text-[#F5F5F5]"
            >
              <option value="editor">Editor</option>
              <option value="owner">Owner</option>
            </select>
          </Field>
        </div>
        <button
          type="submit"
          disabled={creating}
          data-testid="users-create-submit"
          className="btn-gold mt-3 inline-flex items-center gap-2 rounded-full px-6 py-3 text-xs font-bold uppercase tracking-wider disabled:opacity-60"
        >
          {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          Crea admin
        </button>
      </form>
    </div>
  );
}
